/**
 * UserRow Component
 *
 * Table row for displaying user information in the admin list.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { User } from '../hooks';
import { canSendRetrieveEmail, openRetrieveAccountEmail } from '../retrieveAccountEmail';

// Helper to format relative time
const formatRelativeTime = (date: Date | undefined): string => {
  if (!date) return 'Never';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface UserRowProps {
  user: User;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onAction: (action: 'view' | 'role' | 'suspend' | 'unsuspend') => void;
}

export const UserRow: React.FC<UserRowProps> = ({
  user,
  isSelected,
  onSelect,
  onAction,
}) => {
  const roleColors: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800',
    host: 'bg-blue-100 text-blue-800',
    guest: 'bg-gray-100 text-gray-800',
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      {/* Checkbox */}
      <td className="p-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
        />
      </td>

      {/* User Info */}
      <td className="p-4">
        <Link
          to={`/admin/users/${user.id}`}
          className="flex items-center gap-3 hover:text-primary-600"
        >
          <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary-100 text-primary-600 font-medium">
                {user.displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{user.displayName}</p>
            <p className="text-sm text-gray-500 truncate">{user.email}</p>
          </div>
        </Link>
      </td>

      {/* Role */}
      <td className="p-4">
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            roleColors[user.role]
          }`}
        >
          {user.role}
        </span>
      </td>

      {/* Status */}
      <td className="p-4">
        <div className="flex flex-col items-start gap-1">
          {user.isSuspended ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              Suspended
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Active
            </span>
          )}
          {user.wpUserId &&
            (user.accountRetrieved ? (
              <span
                className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-100 text-blue-800"
                title={
                  user.accountRetrievedAt
                    ? `Retrieved ${user.accountRetrievedAt.toLocaleDateString()}${
                        user.retrievalMethod ? ` via ${user.retrievalMethod}` : ''
                      }`
                    : 'Account claimed by the user'
                }
              >
                Legacy: retrieved
              </span>
            ) : (
              <span
                className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-800"
                title="Imported from the old database; the user has never claimed it"
              >
                Legacy: not retrieved
              </span>
            ))}
        </div>
      </td>

      {/* Registered */}
      <td className="p-4 text-sm text-gray-500 hidden md:table-cell">{formatDate(user.createdAt)}</td>

      {/* Last Login */}
      <td className="p-4 text-sm text-gray-500 hidden lg:table-cell">
        {formatRelativeTime(user.lastLoginAt)}
      </td>

      {/* Actions */}
      <td className="p-4">
        <div className="flex items-center gap-2">
          <Link
            to={`/admin/users/${user.id}`}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <EyeIcon className="w-4 h-4" />
          </Link>
          <button
            onClick={() => onAction('role')}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Change Role"
          >
            <UserIcon className="w-4 h-4" />
          </button>
          {/* Only legacy hosts who never claimed their account get chased. */}
          {canSendRetrieveEmail(user) && (
            <button
              onClick={() => openRetrieveAccountEmail(user)}
              className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded transition-colors"
              title="Send email to retrieve account"
            >
              <MailIcon className="w-4 h-4" />
            </button>
          )}
          {user.isSuspended ? (
            <button
              onClick={() => onAction('unsuspend')}
              className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
              title="Unsuspend"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => onAction('suspend')}
              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Suspend"
            >
              <BanIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Icons
const EyeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MailIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const BanIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default UserRow;
